import { Link } from "react-router-dom";
import LandingNav from "@/pages/home/components/LandingNav";

const releases = [
  {
    version: "v2.4.0",
    date: "May 18, 2026",
    tag: "Latest",
    sections: [
      {
        label: "New Features",
        items: [
          "SEO Manager — full control over meta tags, Open Graph, keywords, sitemap, and robots.txt from the admin panel",
          "Deployment Status Dashboard for super admins — real-time monitoring of frontend, API, SSL, domains, cron jobs, and workers",
          "React Error Boundaries across admin, SA, and portal routes — graceful fallback UI instead of white screens on crashes",
        ],
      },
      {
        label: "Improvements",
        items: [
          "Enhanced Google Search snippet preview in SEO Manager with real-time character counting",
          "Open Graph preview card now shows how links appear when shared on Facebook, WhatsApp, and LinkedIn",
          "Keyword chips with interactive add/remove and best-practice tips for local photography SEO",
          "Sitemap XML and robots.txt viewers with syntax highlighting and copy-to-clipboard",
        ],
      },
    ],
  },
  {
    version: "v2.3.0",
    date: "May 10, 2026",
    sections: [
      {
        label: "New Features",
        items: [
          "AI Conversations module — unified inbox for all client communications across WhatsApp, email, and portal messages",
          "Link Client modal — connect existing conversations to client profiles for complete communication history",
          "Conversation detail view with message threading, timestamps, and client context sidebar",
        ],
      },
      {
        label: "Improvements",
        items: [
          "Notification dropdown now groups alerts by type and supports mark-all-read",
          "Email Queue page with status tracking, retry controls, and delivery analytics",
          "Integration Queue for monitoring WhatsApp worker status and message processing",
          "Appointment settings with calendar sync and availability windows",
        ],
      },
    ],
  },
  {
    version: "v2.2.0",
    date: "April 28, 2026",
    sections: [
      {
        label: "New Features",
        items: [
          "Signature Books — digital guest books with photo capture, messages, and PDF export for events",
          "Guest Book public page with signature canvas, photo picker, and real-time entry display",
          "Portal Signature Book tab — clients can view, print, and manage their event guest book entries",
        ],
      },
      {
        label: "Improvements",
        items: [
          "Contract detail page redesigned with tabbed navigation for Overview, Text, Signature, Files, Payments, Add-ons, and Activity",
          "Payment status badges with configurable thresholds for paid, partial, overdue, and pending states",
          "Storage usage tracking per tenant with visual progress bars and VPS configuration settings",
        ],
      },
    ],
  },
  {
    version: "v2.1.0",
    date: "April 12, 2026",
    sections: [
      {
        label: "New Features",
        items: [
          "Digital Invitations module — create, customize, and send beautiful event invitations with RSVP tracking",
          "Invitation Templates library with pre-designed themes for weddings, quinceañeras, corporate events, and birthdays",
          "Music library for invitations — upload and manage background tracks for invitation pages",
        ],
      },
      {
        label: "Improvements",
        items: [
          "Client portal redesigned with Summary Cards, Active Features widget, and section-based navigation",
          "Job workflow with drag-and-drop task reordering and customizable step templates",
          "Contract Add-ons system — attach signature books, invitations, and media packages directly to contracts",
        ],
      },
    ],
  },
  {
    version: "v2.0.0",
    date: "March 20, 2026",
    sections: [
      {
        label: "New Features",
        items: [
          "Complete platform redesign with dark theme, gold accents, and improved navigation",
          "Super Admin dashboard with tenant management, billing overview, module requests, and audit logs",
          "Multi-tenant architecture — each photography studio gets an isolated workspace with custom branding",
        ],
      },
      {
        label: "Breaking Changes",
        items: [
          "Legacy single-tenant accounts migrated to new multi-tenant infrastructure",
          "API endpoints updated to include tenant context in all requests",
          "Previous contract templates need manual migration to new template engine",
        ],
      },
    ],
  },
  {
    version: "v1.5.0",
    date: "February 8, 2026",
    sections: [
      {
        label: "New Features",
        items: [
          "Automations engine — trigger actions based on lead status, payment events, and job milestones",
          "Reminders system with email and in-app notifications for upcoming sessions, pending payments, and contract expirations",
          "Lead management with conversion tracking and activity timeline",
        ],
      },
      {
        label: "Improvements",
        items: [
          "Payment tracking expanded to support partial payments, deposits, and payment schedules",
          "Client detail page with tabbed layout and complete history across contracts, jobs, payments, and messages",
        ],
      },
    ],
  },
  {
    version: "v1.0.0",
    date: "January 15, 2026",
    sections: [
      {
        label: "Initial Release",
        items: [
          "Client management with profiles, contact info, and activity tracking",
          "Digital contracts with e-signatures and template system",
          "Job workflow with customizable steps and progress tracking",
          "Payment tracking with multiple payment method support",
          "Client portal with contract viewing, payment, and messaging",
          "Basic branding customization for studio name, logo, and colors",
        ],
      },
    ],
  },
];

export default function ChangelogPage() {
  return (
    <div className="min-h-screen bg-[#0A0B0F] text-white font-sans">
      <LandingNav />

      <section className="pt-28 pb-20 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="mb-16">
            <p className="text-[#C8A96E] text-xs font-semibold uppercase tracking-widest mb-3">Product</p>
            <h1 className="text-4xl md:text-5xl font-black mb-4">Changelog</h1>
            <p className="text-white/40 text-base">
              Every update, improvement, and new feature we ship to make Pixory AI the best platform for photography studios.
            </p>
          </div>

          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-0 top-0 bottom-0 w-px bg-white/10 hidden md:block"></div>

            <div className="space-y-12">
              {releases.map((release, idx) => (
                <div key={release.version} className="relative md:pl-10">
                  {/* Timeline dot */}
                  <div className="hidden md:flex absolute left-0 top-1.5 -translate-x-1/2 w-3 h-3 items-center justify-center">
                    <div className={`w-2.5 h-2.5 rounded-full border-2 ${idx === 0 ? "bg-[#C8A96E] border-[#C8A96E]" : "bg-[#141418] border-white/20"}`}></div>
                  </div>

                  {/* Header */}
                  <div className="flex flex-wrap items-baseline gap-3 mb-5">
                    <h2 className="text-xl font-black">{release.version}</h2>
                    <span className="text-white/20 text-sm">{release.date}</span>
                    {release.tag && (
                      <span className="bg-[#C8A96E]/15 text-[#C8A96E] text-[10px] font-bold px-2.5 py-0.5 rounded-full uppercase tracking-wider">
                        {release.tag}
                      </span>
                    )}
                  </div>

                  {/* Sections */}
                  <div className="space-y-4">
                    {release.sections.map((section) => (
                      <div key={section.label} className="bg-[#141418] rounded-xl border border-white/5 p-5">
                        <h3 className={`text-xs font-semibold uppercase tracking-wider mb-3 ${
                          section.label === "Breaking Changes"
                            ? "text-red-400"
                            : section.label === "New Features"
                            ? "text-emerald-400"
                            : "text-white/40"
                        }`}>
                          {section.label}
                        </h3>
                        <ul className="space-y-2">
                          {section.items.map((item, i) => (
                            <li key={i} className="flex items-start gap-2.5 text-sm text-white/60">
                              <span className="w-4 h-4 flex items-center justify-center text-white/20 mt-0.5 shrink-0">
                                <i className="ri-arrow-right-s-line text-xs"></i>
                              </span>
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Subscribe CTA */}
          <div className="mt-16 bg-[#141418] rounded-2xl border border-white/5 p-8 text-center">
            <span className="w-12 h-12 flex items-center justify-center rounded-full bg-[#C8A96E]/10 text-[#C8A96E] mx-auto mb-4">
              <i className="ri-notification-3-line text-lg"></i>
            </span>
            <h3 className="text-white font-bold text-lg mb-2">Stay up to date</h3>
            <p className="text-white/40 text-sm mb-5 max-w-md mx-auto">
              New features and improvements are shipped regularly. Follow our changelog to never miss an update.
            </p>
            <Link
              to="/register"
              className="inline-flex items-center gap-2 bg-[#C8A96E] hover:bg-[#b8965a] text-black text-sm font-semibold px-6 py-2.5 rounded-full transition-colors cursor-pointer whitespace-nowrap"
            >
              Create Free Account
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-arrow-right-line text-xs"></i>
              </span>
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/5 py-8 px-6">
        <div className="max-w-3xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <Link to="/" className="text-white/30 hover:text-white/60 text-xs transition-colors">← Back to Pixory AI</Link>
          <span className="text-white/20 text-xs">© 2025 Pixory AI. All rights reserved.</span>
        </div>
      </footer>
    </div>
  );
}